import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Download, Upload, RefreshCw, Settings } from 'lucide-react';
import { Button } from '../ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import toast from 'react-hot-toast';

interface ConversionOptions {
  delimiter: string;
  includeHeaders: boolean;
  flattenObjects: boolean;
  arrayHandling: 'join' | 'separate' | 'index';
  arraySeparator: string;
}

export const JsonToCsvTool: React.FC = () => {
  const { t } = useTranslation();
  const [jsonInput, setJsonInput] = useState('');
  const [csvOutput, setCsvOutput] = useState('');
  const [options, setOptions] = useState<ConversionOptions>({
    delimiter: ',',
    includeHeaders: true,
    flattenObjects: true,
    arrayHandling: 'join',
    arraySeparator: ';'
  });
  const [isConverting, setIsConverting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const flattenObject = (obj: any, prefix = ''): any => {
    const flattened: any = {};
    
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const newKey = prefix ? `${prefix}.${key}` : key;
        const value = obj[key];
        
        if (value === null || value === undefined) {
          flattened[newKey] = '';
        } else if (Array.isArray(value)) {
          if (options.arrayHandling === 'join') {
            flattened[newKey] = value.join(options.arraySeparator);
          } else if (options.arrayHandling === 'separate') {
            value.forEach((item, index) => {
              flattened[`${newKey}[${index}]`] = item;
            });
          } else {
            // index handling
            flattened[newKey] = JSON.stringify(value);
          }
        } else if (typeof value === 'object') {
          if (options.flattenObjects) {
            Object.assign(flattened, flattenObject(value, newKey));
          } else {
            flattened[newKey] = JSON.stringify(value);
          }
        } else {
          flattened[newKey] = value;
        }
      }
    }
    
    return flattened;
  };

  const escapeCSVField = (field: string): string => {
    const stringField = String(field);
    if (stringField.includes(options.delimiter) || stringField.includes('"') || stringField.includes('\n')) {
      return `"${stringField.replace(/"/g, '""')}"`;
    }
    return stringField;
  };

  const convertJsonToCsv = async () => {
    if (!jsonInput.trim()) {
      toast.error('Please enter JSON data');
      return;
    }

    setIsConverting(true);
    
    try {
      const jsonData = JSON.parse(jsonInput);
      
      // Ensure we have an array
      let dataArray: any[];
      if (Array.isArray(jsonData)) {
        dataArray = jsonData;
      } else if (typeof jsonData === 'object') {
        dataArray = [jsonData];
      } else {
        throw new Error('JSON must be an object or array of objects');
      }

      if (dataArray.length === 0) {
        setCsvOutput('');
        toast.success('Empty JSON converted to empty CSV');
        return;
      }

      // Flatten objects if option is enabled
      const processedData = dataArray.map(item => 
        options.flattenObjects ? flattenObject(item) : item
      );

      // Get all unique keys
      const allKeys = new Set<string>();
      processedData.forEach(item => {
        Object.keys(item).forEach(key => allKeys.add(key));
      });
      
      const headers = Array.from(allKeys).sort();
      
      // Build CSV
      let csv = '';
      
      // Add headers if option is enabled
      if (options.includeHeaders) {
        csv += headers.map(header => escapeCSVField(header)).join(options.delimiter) + '\n';
      }
      
      // Add data rows
      processedData.forEach(item => {
        const row = headers.map(header => {
          const value = item[header];
          return escapeCSVField(value !== undefined ? value : '');
        });
        csv += row.join(options.delimiter) + '\n';
      });
      
      setCsvOutput(csv);
      toast.success('JSON converted to CSV successfully');
    } catch (error) {
      console.error('Conversion error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to convert JSON to CSV');
      setCsvOutput('');
    } finally {
      setIsConverting(false);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
        toast.error('Please select a JSON file');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setJsonInput(content);
      };
      reader.readAsText(file);
    }
  };

  const downloadCsv = () => {
    if (!csvOutput) {
      toast.error('No CSV data to download');
      return;
    }

    const blob = new Blob([csvOutput], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'converted-data.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const loadSampleData = () => {
    const sampleJson = `[
  {
    "name": "John Doe",
    "age": 30,
    "address": {
      "street": "123 Main St",
      "city": "New York",
      "country": "USA"
    },
    "hobbies": ["reading", "swimming", "coding"],
    "active": true
  },
  {
    "name": "Jane Smith",
    "age": 25,
    "address": {
      "street": "456 Oak Ave",
      "city": "London",
      "country": "UK"
    },
    "hobbies": ["painting", "traveling"],
    "active": false
  }
]`;
    setJsonInput(sampleJson);
  };

  const clearAll = () => {
    setJsonInput('');
    setCsvOutput('');
  };

  const delimiters = [
    { value: ',', label: 'Comma (,)' },
    { value: ';', label: 'Semicolon (;)' },
    { value: '\t', label: 'Tab' },
    { value: '|', label: 'Pipe (|)' }
  ];

  const arrayHandlingOptions = [
    { value: 'join', label: 'Join with separator' },
    { value: 'separate', label: 'Separate columns' },
    { value: 'index', label: 'JSON string' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileText className="w-5 h-5" />
            <span>{t('common.jsonToCsvConverter')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => fileInputRef.current?.click()}
              variant="outline"
            >
              <Upload className="w-4 h-4 mr-2" />
              {t('common.uploadJsonFile')}
            </Button>
            <Button
              onClick={loadSampleData}
              variant="outline"
            >
              {t('common.loadSampleData')}
            </Button>
            <Button
              onClick={clearAll}
              variant="outline"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Clear All
            </Button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json,application/json"
            onChange={handleFileUpload}
            className="hidden"
          />
        </CardContent>
      </Card>

      {/* Conversion Options */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Settings className="w-5 h-5" />
            <span>Conversion Options</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Delimiter
                </label>
                <select
                  value={options.delimiter}
                  onChange={(e) => setOptions({ ...options, delimiter: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  {delimiters.map((delimiter) => (
                    <option key={delimiter.value} value={delimiter.value}>
                      {delimiter.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Array Handling
                </label>
                <select
                  value={options.arrayHandling}
                  onChange={(e) => setOptions({ ...options, arrayHandling: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  {arrayHandlingOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {options.arrayHandling === 'join' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Array Separator
                  </label>
                  <input
                    type="text"
                    value={options.arraySeparator}
                    onChange={(e) => setOptions({ ...options, arraySeparator: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    placeholder=";"
                  />
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={options.includeHeaders}
                    onChange={(e) => setOptions({ ...options, includeHeaders: e.target.checked })}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm">Include column headers</span>
                </label>
                
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={options.flattenObjects}
                    onChange={(e) => setOptions({ ...options, flattenObjects: e.target.checked })}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm">Flatten nested objects</span>
                </label>
              </div>

              <div className="text-sm text-gray-600 dark:text-gray-400">
                <p><strong>Array Handling:</strong></p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li><strong>Join:</strong> Combine array items with separator</li>
                  <li><strong>Separate:</strong> Create separate columns for each item</li>
                  <li><strong>JSON string:</strong> Keep as JSON string</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Input/Output */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* JSON Input */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>{t('common.jsonInput')}</span>
              <Button
                onClick={convertJsonToCsv}
                disabled={!jsonInput.trim() || isConverting}
                variant="primary"
                size="sm"
              >
                {isConverting ? t('common.converting') : t('common.convertToCsv')}
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <textarea
              value={jsonInput}
              onChange={(e) => setJsonInput(e.target.value)}
              placeholder="Paste your JSON data here or upload a file..."
              className="w-full h-64 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white font-mono text-sm"
            />
          </CardContent>
        </Card>

        {/* CSV Output */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>{t('common.csvOutput')}</span>
              {csvOutput && (
                <Button
                  onClick={downloadCsv}
                  variant="outline"
                  size="sm"
                >
                  <Download className="w-3 h-3 mr-1" />
                  Download
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <textarea
              value={csvOutput}
              readOnly
              placeholder="CSV output will appear here..."
              className="w-full h-64 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-white font-mono text-sm"
            />
          </CardContent>
        </Card>
      </div>

      {/* Usage Guide */}
      <Card>
        <CardHeader>
          <CardTitle>Usage Guide</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">JSON Format Requirements:</h4>
              <ul className="space-y-1 text-gray-600 dark:text-gray-400">
                <li>• Must be valid JSON format</li>
                <li>• Can be a single object or array of objects</li>
                <li>• Nested objects will be flattened (optional)</li>
                <li>• Arrays can be handled in different ways</li>
                <li>• Null values become empty strings</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Output Features:</h4>
              <ul className="space-y-1 text-gray-600 dark:text-gray-400">
                <li>• Automatic CSV escaping for special characters</li>
                <li>• Configurable delimiter and array handling</li>
                <li>• Optional column headers</li>
                <li>• Flattened nested object support</li>
                <li>• Download as .csv file</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
